import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, LayoutDashboard, User, Package, Heart, LogOut, UserCheck, ChevronDown, Menu, X } from 'lucide-react';

const Header = ({ cartCount = 0, onOpenCart }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const adminToken = localStorage.getItem('adminToken');
  const userToken = localStorage.getItem('userToken');
  const userJson = localStorage.getItem('user');
  let userData = null;
  try {
    if (userJson) userData = JSON.parse(userJson);
  } catch (e) {}

  const isAdminLoggedIn = Boolean(adminToken);
  const isUserLoggedIn = Boolean(userToken || (userData && userData.role !== 'admin'));

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    navigate('/');
    window.location.reload();
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop' },
    { name: 'Science', path: '/science' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <header
      className="web-header-bar"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        height: '76px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      {/* Brand Logo */}
      <Link to="/" className="web-brand-logo" onClick={() => setMobileMenuOpen(false)}>
        <div className="brand-dots-grid">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="brand-dot-red"></div>
          ))}
        </div>
        <div className="brand-text-col">
          <span className="brand-main-name">INFRAEDGE</span>
          <span className="brand-sub-name">RED LIGHT THERAPY</span>
        </div>
      </Link>

      {/* Desktop Navigation Links */}
      <nav className="desktop-nav-only" style={{ display: 'flex', alignItems: 'center' }}>
        <ul className="web-nav-links">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <li key={link.name}>
                <Link to={link.path} className={`web-nav-link ${isActive ? 'active' : ''}`}>
                  {link.name}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Header Actions (Cart, Login/Account, Hamburger on Mobile) */}
      <div className="web-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        
        {/* Shopping Cart Icon (Navigates to /cart page) */}
        <Link to="/cart" className="icon-btn-ghost" title="View Shopping Cart" style={{ position: 'relative', textDecoration: 'none', padding: '6px' }}>
          <ShoppingBag size={22} />
          {cartCount > 0 && <span className="cart-count-badge">{cartCount}</span>}
        </Link>

        {/* Desktop Account / Admin / Login Button */}
        <div className="desktop-nav-only" style={{ display: 'flex', alignItems: 'center' }}>
          {isAdminLoggedIn ? (
            <Link
              to="/admin/dashboard"
              style={{
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: '#dc2626',
                color: '#ffffff',
                padding: '10px 20px',
                borderRadius: '9999px',
                fontSize: '13.5px',
                fontWeight: 800,
                boxShadow: '0 4px 14px rgba(220, 38, 38, 0.4)'
              }}
            >
              <LayoutDashboard size={18} />
              <span>Admin Dashboard</span>
            </Link>
          ) : isUserLoggedIn ? (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  padding: '8px 14px',
                  borderRadius: '9999px',
                  color: '#dc2626',
                  fontWeight: 800,
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                <UserCheck size={18} />
                <span>{userData?.name ? userData.name.split(' ')[0] : 'Account'}</span>
                <ChevronDown size={14} style={{ transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
              </button>

              {/* Account Dropdown Menu */}
              {userMenuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    width: '200px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '14px',
                    padding: '8px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
                    zIndex: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}
                >
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9', marginBottom: '4px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>{userData?.name || 'Customer Account'}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userData?.email || ''}</div>
                  </div>

                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', textDecoration: 'none', color: '#0f172a', fontSize: '13px', fontWeight: 700 }}
                  >
                    <User size={16} color="#dc2626" />
                    <span>Profile</span>
                  </Link>

                  <Link
                    to="/orders"
                    onClick={() => setUserMenuOpen(false)}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', textDecoration: 'none', color: '#0f172a', fontSize: '13px', fontWeight: 700 }}
                  >
                    <Package size={16} color="#dc2626" />
                    <span>My Orders</span>
                  </Link>

                  <Link
                    to="/wishlist"
                    onClick={() => setUserMenuOpen(false)}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', textDecoration: 'none', color: '#0f172a', fontSize: '13px', fontWeight: 700 }}
                  >
                    <Heart size={16} color="#dc2626" />
                    <span>Wishlist</span>
                  </Link>

                  <button
                    onClick={handleLogout}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', border: 'none', background: '#fef2f2', color: '#dc2626', fontSize: '13px', fontWeight: 800, cursor: 'pointer', textAlign: 'left', marginTop: '4px' }}
                  >
                    <LogOut size={16} color="#dc2626" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="btn-red-pill"
              style={{
                textDecoration: 'none',
                padding: '10px 22px',
                fontSize: '13.5px',
                fontWeight: 800
              }}
            >
              Login
            </Link>
          )}
        </div>

        {/* Mobile Hamburger Button (Shown on mobile/tablet) */}
        <button
          className="mobile-hamburger-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{
            background: 'none',
            border: 'none',
            color: '#0f172a',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '8px',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Toggle Navigation Menu"
        >
          {mobileMenuOpen ? <X size={26} color="#dc2626" /> : <Menu size={26} color="#0f172a" />}
        </button>

      </div>

      {/* Mobile Navigation Drawer Overlay */}
      {mobileMenuOpen && (
        <div
          style={{
            position: 'fixed',
            top: '76px',
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            padding: '24px',
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '24px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            overflowY: 'auto'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '20px' }}>
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      style={{
                        fontSize: '18px',
                        fontWeight: 800,
                        color: isActive ? '#dc2626' : '#0f172a',
                        textDecoration: 'none',
                        display: 'block',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        background: isActive ? '#fef2f2' : 'transparent',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {link.name}
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Account / Login actions in Mobile Menu */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {isAdminLoggedIn ? (
                <Link
                  to="/admin/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ background: '#dc2626', color: '#ffffff', textDecoration: 'none', padding: '14px', borderRadius: '12px', fontWeight: 800, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                  <LayoutDashboard size={18} />
                  <span>Go to Admin Dashboard</span>
                </Link>
              ) : isUserLoggedIn ? (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 800, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    Signed in as {userData?.name || 'Customer'}
                  </div>
                  <Link to="/profile" onClick={() => setMobileMenuOpen(false)} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <User size={18} color="#dc2626" />
                    <span>Profile Details</span>
                  </Link>
                  <Link to="/orders" onClick={() => setMobileMenuOpen(false)} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Package size={18} color="#dc2626" />
                    <span>My Orders</span>
                  </Link>
                  <Link to="/wishlist" onClick={() => setMobileMenuOpen(false)} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Heart size={18} color="#dc2626" />
                    <span>Saved Wishlist</span>
                  </Link>
                  <button onClick={handleLogout} style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '12px', borderRadius: '10px', fontWeight: 800, cursor: 'pointer', textAlign: 'center', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <LogOut size={16} />
                    <span>Logout Account</span>
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ background: '#dc2626', color: '#ffffff', textDecoration: 'none', padding: '14px', borderRadius: '12px', fontWeight: 800, textAlign: 'center', fontSize: '16px', boxShadow: '0 4px 14px rgba(220, 38, 38, 0.3)' }}
                >
                  Login / Register Account
                </Link>
              )}
            </div>
          </div>
          
          <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center' }}>
            © 2026 InfraEdge Red Light Therapy. All rights reserved.
          </div>
        </div>
      )}

    </header>
  );
};

export default Header;
